import { useEffect, useState } from "react";
import { XMarkIcon } from "@heroicons/react/24/solid";
import { cn } from "@utils";
import TaggedUsers from "@components/TaggedUsers";
import DateTimePicker from "@components/datePickers/DateTimePicker";
import AiSpinner from "./AiSpinner";
import NoteTagEditor from "./NoteTagEditor";
import SuggestionIcon from "./SuggestionIcon";
import SuggestionStatusBadge from "./SuggestionStatusBadge";
import { isComponentDecidable } from "./suggestionComponents";

// Generic card for a registry-driven suggestion component (see suggestionComponents.js). Collapsed it
// reads as a one-line sentence with Edit / accept buttons; Edit expands into a roomier form of the
// descriptor's fields, and accept commits the (possibly edited) values as step amendments. This is the
// template future chat components follow — no per-type modal, no bespoke branch in the modal's render.
export default function SuggestionComponentCard({
  suggestion,
  descriptor,
  index = 0,
  isSending = false,
  isDone = false,
  isRejected = false,
  onAccept,
  onReject,
  onGrow,
}) {
  const step = (suggestion.params?.steps ?? []).find(
    (s) => s.stepType === descriptor.stepType,
  );
  const stepId = step?.stepId;

  const [values, setValues] = useState(() => descriptor.seedValues(suggestion));
  const [editing, setEditing] = useState(false);
  // Holds a Send pressed before the action id streamed in, so it can fire once the id lands (see the
  // deferred-accept effect) — the user sees a normal spinner, never the "still preparing" wait.
  const [wantsAccept, setWantsAccept] = useState(null);

  // Reseed when the pre-created step arrives/changes (streaming can render the card before the
  // action-service has stamped the step id and its authoritative payload).
  useEffect(() => {
    setValues(descriptor.seedValues(suggestion));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepId]);

  // Keep the thread pinned to the bottom as the card grows into / out of edit mode.
  useEffect(() => {
    onGrow?.();
  }, [editing, onGrow]);

  const isDecided = isDone || isRejected;
  const decidable = isComponentDecidable(suggestion);
  // The card has streamed in but its action id hasn't landed yet — a transient wait. The card looks
  // ready and the accept is queued (below), so this is never surfaced; only a genuine failure is.
  const preparing = !suggestion.params?.actionId && suggestion.preparing === true;
  const isEditing = editing && !isDecided;

  const setField = (key, value) => setValues((prev) => ({ ...prev, [key]: value }));

  // A required text field must have a value before the suggestion can be committed.
  const missingRequired = descriptor.fields.some(
    (f) => f.input !== "datetime" && f.required !== false && !String(values[f.key] ?? "").trim(),
  );

  // Only for a genuine block — a still-preparing action shows nothing (it looks ready, accept is queued).
  const disabledReason = preparing
    ? null
    : !suggestion.params?.actionId
      ? "This suggestion couldn't be prepared — ask again in the chat."
      : !decidable
        ? "This suggestion has expired — ask again in the chat for a fresh one."
        : null;

  const sendDisabled =
    isSending || Boolean(wantsAccept) || missingRequired || (!decidable && !preparing);

  // Deferred accept: fire a Send pressed while the action was still preparing the moment its id lands;
  // if the turn finishes with no action, drop it (the genuine failed state then shows).
  useEffect(() => {
    if (!wantsAccept) return;
    if (suggestion.params?.actionId) {
      onAccept?.(wantsAccept.values);
      setWantsAccept(null);
    } else if (!preparing) {
      setWantsAccept(null);
    }
  }, [wantsAccept, suggestion.params?.actionId, preparing, onAccept]);

  // The corner X (only shown while the suggestion is undecided) either cancels an in-progress edit —
  // returning the card to its initial state — or rejects the suggestion so the accept button flips to
  // a crossed badge, matching the promise-to-pay / payment-plan flow. Once decided the X is gone and
  // the card stays put.
  const handleDismiss = () => {
    if (isEditing) {
      setEditing(false);
      setValues(descriptor.seedValues(suggestion));
      return;
    }
    onReject?.();
  };

  const sentence = descriptor.summary?.(values) ?? "";

  const acceptButton = (
    <button
      type="button"
      onClick={() => {
        // Queue the accept while the action is still preparing; fire it now once it's ready.
        if (preparing) setWantsAccept({ values });
        else onAccept?.(values);
      }}
      disabled={sendDisabled}
      title={!isSending && disabledReason ? disabledReason : undefined}
      className="flex min-w-[2.25rem] items-center justify-center rounded text-sm font-semibold text-zinc-900 hover:text-zinc-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 disabled:cursor-not-allowed disabled:text-zinc-300"
    >
      {isSending || wantsAccept ? <AiSpinner size={18} /> : descriptor.buttonLabel}
    </button>
  );

  return (
    <div
      className="animate-fadeIn relative rounded-2xl border border-zinc-200 bg-white px-5 pb-5 pt-6 shadow-sm"
      style={{ animationDelay: `${index * 90}ms` }}
    >
      {/* Hidden once decided so a rejected/accepted card can't be cleared away — it always stays. */}
      {!isDecided && (
        <button
          type="button"
          onClick={handleDismiss}
          disabled={isSending || Boolean(wantsAccept)}
          aria-label="Dismiss suggestion"
          className="absolute right-4 top-3 text-zinc-400 hover:text-zinc-600 disabled:cursor-not-allowed disabled:opacity-40"
        >
          <XMarkIcon aria-hidden="true" className="size-3.5" />
        </button>
      )}

      {isEditing ? (
        /* --- Edit: a roomy, form-like layout --- */
        <div className="flex flex-col gap-2.5">
          <div className="flex items-center gap-3">
            <Glyph descriptor={descriptor} type={suggestion.type} />
            <div className="flex-1 text-xs font-medium text-zinc-500">
              {descriptor.title}
            </div>
            {acceptButton}
          </div>

          <div className="flex flex-col gap-2">
            <div className="flex flex-wrap items-center gap-3">
              {descriptor.fields.map((field) => (
                <Field
                  key={field.key}
                  field={field}
                  value={values[field.key] ?? ""}
                  disabled={isSending}
                  onChange={(v) => setField(field.key, v)}
                />
              ))}
            </div>
            {disabledReason && <div className="text-xs text-amber-600">{disabledReason}</div>}
          </div>
        </div>
      ) : (
        /* --- Collapsed / decided: the sentence wraps rather than truncating; the row stays
           vertically centred so the glyph and Edit/Create actions sit centred even when it's taller --- */
        <div className="flex items-center gap-3">
          <Glyph descriptor={descriptor} type={suggestion.type} />

          <div className="min-w-0 flex-1">
            <div className="text-xs font-medium text-zinc-500">{descriptor.title}</div>
            <div className="mt-1 break-words text-sm text-zinc-700">{sentence}</div>
            {values.assignees?.length > 0 && <TaggedUsers users={values.assignees} />}
            {!isDecided && disabledReason && (
              <div className="mt-1 text-xs text-amber-600">{disabledReason}</div>
            )}
          </div>

          {isDecided ? (
            <SuggestionStatusBadge done={isDone} />
          ) : (
            <div className="flex shrink-0 items-center gap-4">
              <button
                type="button"
                onClick={() => setEditing(true)}
                disabled={isSending}
                className="text-sm font-semibold text-zinc-900 hover:text-zinc-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 disabled:cursor-not-allowed disabled:text-zinc-300"
              >
                Edit
              </button>
              {acceptButton}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// The descriptor's own tinted tile (a task's fuchsia checkbox), else the shared AI-gradient sparkle.
function Glyph({ descriptor, type }) {
  const Icon = descriptor.Icon;
  if (Icon) {
    return (
      <span
        className={cn(
          "flex size-9 shrink-0 items-center justify-center rounded-xl",
          descriptor.iconTile,
        )}
      >
        <Icon className="size-[18px]" />
      </span>
    );
  }
  return (
    <span className="shrink-0 rounded-xl bg-[linear-gradient(135deg,#ec4899,#a855f7,#3b82f6,#22d3ee,#fb923c)] p-px">
      <span className="flex size-9 items-center justify-center rounded-[11px] bg-white">
        <SuggestionIcon type={type} className="size-5" />
      </span>
    </span>
  );
}

function Field({ field, value, disabled, onChange }) {
  if (field.input === "assignees") {
    // The same tag editor a note uses — removable colleague chips + the "add colleague" picker.
    return <NoteTagEditor users={value ?? []} onChange={onChange} label={field.addLabel} />;
  }

  if (field.input === "datetime") {
    // anchor="bottom end" right-aligns the popover so it opens inward from the narrow note panel.
    return (
      <DateTimePicker value={value} disabled={disabled} onChange={onChange} anchor="bottom end" />
    );
  }

  if (field.input === "textarea") {
    return (
      <textarea
        rows={2}
        value={value}
        disabled={disabled}
        placeholder={field.placeholder}
        aria-label={field.label}
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus={field.autoFocus}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-lg border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus:outline focus:outline-1 focus:-outline-offset-1 focus:outline-primary disabled:bg-zinc-50 disabled:text-zinc-400"
      />
    );
  }

  return (
    <input
      type="text"
      value={value}
      disabled={disabled}
      placeholder={field.placeholder}
      aria-label={field.label}
      // eslint-disable-next-line jsx-a11y/no-autofocus
      autoFocus={field.autoFocus}
      onChange={(e) => onChange(e.target.value)}
      className={cn(
        "rounded-lg border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus:outline focus:outline-1 focus:-outline-offset-1 focus:outline-primary disabled:bg-zinc-50 disabled:text-zinc-400",
        field.grow ? "min-w-[8rem] flex-1" : "w-full",
      )}
    />
  );
}
